import { type HTMLAttributes, type ReactNode, memo } from 'react';

import { PrimaryButton } from '@pulse-web-ui/button-v2';
import { Link as LinkIcon } from '@pulse-web-ui/icons';
import { useTheme } from '@pulse-web-ui/theme';

import { addTestAttribute } from '../../utils';
import { ColumnContainer, RowContainer } from '../containers';
import { TextLink } from '../links';
import type { StatusType } from '../status';
import { Status } from '../status';
import {
  Description,
  IconContainer,
  Title,
  Wrapper,
} from './info-banner.styles';

interface LinkInfo {
  text: string;
  to: string;
  isExternal?: boolean;
  onLinkClick?: () => void;
}

interface StatusInfo {
  type: StatusType;
  text: string;
}

export interface InfoBannerProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  status?: StatusInfo;
  description?: ReactNode;
  link?: LinkInfo;
  onButtonClick?: () => void;
  buttonText?: string;
  testId?: string;
  hasBackground?: boolean;
}

export const InfoBanner = memo(
  ({
    className,
    title,
    description,
    status,
    link,
    buttonText,
    hasBackground,
    testId = 'info-banner',
    onButtonClick,
  }: InfoBannerProps) => {
    const theme = useTheme();

    return (
      <Wrapper
        className={className}
        $hasBackground={hasBackground}
      >
        <ColumnContainer>
          {title && (
            <RowContainer
              $marginBottom={0}
              $gap={8}
            >
              <Title>{title}</Title>
              {status && <Status type={status.type}>{status.text}</Status>}
            </RowContainer>
          )}
          {description && <Description>{description}</Description>}
          {link && (
            <span onClick={link.onLinkClick}>
              <TextLink
                href={link.to}
                target='_blank'
                {...addTestAttribute(`${testId}-link`)}
              >
                {link.text}
              </TextLink>
              {link.isExternal && (
                <IconContainer>
                  <LinkIcon
                    width={16}
                    color={theme.colors.text.interactive}
                  />
                </IconContainer>
              )}
            </span>
          )}
        </ColumnContainer>
        {onButtonClick && buttonText && (
          <PrimaryButton
            size='m'
            onClick={onButtonClick}
            {...addTestAttribute(`${testId}-button`)}
          >
            {buttonText}
          </PrimaryButton>
        )}
      </Wrapper>
    );
  }
);
